import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { CalendarDots, Clock, Check, Users } from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00', '13:00', '14:00',
  '15:00', '16:00', '17:00', '18:00', '19:00', '20:00', '21:00', '22:00'
];

export default function CalendarPage() {
  const [selectedDay, setSelectedDay] = useState(0);
  const [availability, setAvailability] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchAvailability = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/availability`, { withCredentials: true });
      const availMap = {};
      (data.slots || []).forEach(slot => {
        availMap[slot.day_of_week] = slot.time_slots;
      });
      setAvailability(availMap);
    } catch (error) {
      console.error('Error fetching availability:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const selectedSlots = availability[selectedDay] || [];

  const toggleTimeSlot = (time) => {
    const newSlots = selectedSlots.includes(time)
      ? selectedSlots.filter(t => t !== time)
      : [...selectedSlots, time].sort();

    setAvailability(prev => ({ ...prev, [selectedDay]: newSlots }));
  };

  const saveAvailability = async () => {
    setSaving(true);
    try {
      const slots = Object.entries(availability)
        .filter(([_, times]) => times && times.length > 0)
        .map(([day, time_slots]) => ({
          day_of_week: parseInt(day),
          time_slots
        }));

      await axios.put(`${API_URL}/api/availability`, { slots }, { withCredentials: true });
      toast.success('Availability saved!');
    } catch (error) {
      toast.error('Failed to save availability');
    } finally {
      setSaving(false);
    }
  };

  const clearDay = () => {
    setAvailability(prev => {
      const updated = { ...prev };
      delete updated[selectedDay];
      return updated;
    });
  };

  return (
    <div className="app-container" data-testid="calendar-page">
      {/* Header */}
      <header className="p-6 pb-4">
        <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            <CalendarDots size={24} weight="duotone" className="inline mr-2 text-[#00F0FF]" />
            My Availability
          </h1>
          <p className="text-[#A1A1AA] text-sm">Set your weekly availability for games</p>
        </motion.div>
      </header>

      {/* Day Selector */}
      <div className="px-6 mb-6">
        <div className="grid grid-cols-7 gap-2">
          {DAYS_OF_WEEK.map((day, index) => {
            const hasSlots = (availability[index] || []).length > 0;
            return (
              <button
                key={day}
                onClick={() => setSelectedDay(index)}
                className={`py-3 rounded-xl text-center transition-all relative ${
                  selectedDay === index
                    ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white'
                    : 'bg-[#0F0F16] text-[#A1A1AA] hover:bg-[#181824]'
                }`}
                data-testid={`day-${day.toLowerCase()}`}
              >
                <span className="text-xs font-medium">{day}</span>
                {hasSlots && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#39FF14]"></div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Time Slots */}
      <div className="px-6 pb-32">
        <motion.div key={selectedDay} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              {DAYS_OF_WEEK[selectedDay]}
            </h2>
            {selectedSlots.length > 0 && (
              <button onClick={clearDay} className="text-[#71717A] hover:text-red-400 transition-colors text-sm">
                Clear
              </button>
            )}
          </div>

          <p className="text-[#A1A1AA] text-sm mb-4">
            <Clock size={14} className="inline mr-1" />
            Select times when you're available to play
          </p>

          {loading ? (
            <div className="grid grid-cols-4 gap-2">
              {[...Array(8)].map((_, i) => (
                <div key={i} className="h-10 rounded-xl bg-[#181824] animate-pulse"></div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2" data-testid="time-slots-grid">
              {TIME_SLOTS.map((time) => {
                const isSelected = selectedSlots.includes(time);
                return (
                  <button
                    key={time}
                    onClick={() => toggleTimeSlot(time)}
                    className={`py-2.5 px-3 rounded-xl font-medium text-sm transition-all ${
                      isSelected
                        ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white shadow-[0_0_15px_rgba(157,76,221,0.3)]'
                        : 'bg-[#181824] text-[#A1A1AA] hover:bg-[#1F1F2E] hover:text-white'
                    }`}
                    data-testid={`time-slot-${time}`}
                  >
                    {time}
                  </button>
                );
              })}
            </div>
          )}

          {/* Summary */}
          <div className="mt-6 p-4 rounded-2xl bg-[#0F0F16] border border-white/5">
            <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
              <Users size={16} weight="duotone" className="text-[#00F0FF]" />
              Weekly Summary
            </h3>
            <div className="space-y-2">
              {DAYS_OF_WEEK.map((day, index) => {
                const slots = availability[index] || [];
                return (
                  <div key={day} className="flex items-center justify-between text-sm">
                    <span className="text-[#71717A]">{day}</span>
                    <span className={slots.length > 0 ? 'text-[#39FF14]' : 'text-[#71717A]'}>
                      {slots.length > 0 ? `${slots.length} slots` : '-'}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Save Button */}
          <motion.button
            onClick={saveAvailability}
            disabled={saving}
            className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
            whileTap={{ scale: 0.98 }}
            data-testid="save-availability"
          >
            {saving ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Saving...
              </span>
            ) : (
              <>
                <Check size={20} weight="bold" />
                Save Availability
              </>
            )}
          </motion.button>
        </motion.div>
      </div>

      <BottomNav />
    </div>
  );
}
